import React, { ChangeEvent, useCallback, useMemo } from 'react';
import {
  Consent,
  ConsentConfig,
  ConsentName,
  useConsentConfigs,
  useHasConsents,
} from '@app/api';
import { Checkbox } from './Checkbox';
import { Trans } from 'react-i18next';
import { useFormikContext } from 'formik';

interface Props {
  name?: string;
  consent: ConsentName | 'CLIENT_SIGNUP' | 'PARTICIPANT_SIGNUP';
  onChange?: (consent?: Consent[]) => any;
  value?: Consent[];
  error?: string;
  alwaysShow?: boolean;
  noMargin?: boolean;
  disabled?: boolean;
}

const consentInConfigs = (consent: Consent, configs: ConsentConfig[]) => {
  return configs.some((config) => config.id === consent.documentId);
};

const configInConsents = (config: ConsentConfig, consents: Consent[]) => {
  return consents.some((consent) => config.id === consent.documentId);
};

const componentNames = {
  [ConsentName.CLIENT_STUDY_TERMS]: 'studyterms',
  [ConsentName.PARTICIPANT_RULES]: 'participationrules',
  [ConsentName.PARTICIPANT_PRIVACY]: 'participantprivacy',
  [ConsentName.PARTICIPANT_TERMS]: 'participantterms',
};

export const ConsentCheckbox = ({
  name,
  consent,
  onChange: onChangeProps,
  value: valueProps,
  alwaysShow,
  ...rest
}: Props) => {
  const configs = useConsentConfigs();

  const formik = useFormikContext();
  const helpers = name ? formik?.getFieldHelpers(name) : undefined;
  const meta = name ? formik?.getFieldMeta<Consent[]>(name) : undefined;
  const setFieldValue = helpers?.setValue;
  const value = valueProps || meta?.value;

  const checkboxConsents = useMemo(() => {
    switch (consent) {
      case 'CLIENT_SIGNUP':
        return [ConsentName.CLIENT_STUDY_TERMS];
      case 'PARTICIPANT_SIGNUP':
        return [ConsentName.PARTICIPANT_PRIVACY, ConsentName.PARTICIPANT_TERMS];
      default:
        return [consent];
    }
  }, [consent]);

  const checkboxConfigs = useMemo(() => {
    return checkboxConsents.map((c) => configs?.[c]).filter(Boolean);
  }, [configs, checkboxConsents]);

  const setChange = onChangeProps || setFieldValue;

  const uncheck = useCallback(() => {
    if (!setChange || !value) {
      return;
    }
    setChange(value.filter((c) => !consentInConfigs(c, checkboxConfigs)));
  }, [checkboxConfigs, setChange, value]);

  const check = useCallback(() => {
    if (!setChange) {
      return;
    }
    const consents = checkboxConfigs.map((config) => {
      return {
        documentId: config.id,
        documentName: config.name,
        date: new Date(),
      };
    });

    setChange([...(value || []), ...consents]);
  }, [checkboxConfigs, setChange, value]);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      if (!checked) {
        uncheck();
      } else {
        check();
      }
    },
    [check, uncheck],
  );

  const checked = useMemo(() => {
    if (!value) {
      return false;
    }
    const vals = Array.isArray(value) ? value : [value];
    return checkboxConfigs.every((config) => configInConsents(config, vals));
  }, [value, checkboxConfigs]);

  const components = useMemo(() => {
    return checkboxConsents.reduce((acc, consent) => {
      const config = configs[consent];
      const url = config?.url || 'https://www.tandemz.io';
      acc[componentNames[consent]] = (
        <a href={url} target="_blank" rel="noopener noreferrer" />
      );
      return acc;
    }, {} as { [key: string]: React.ReactElement });
  }, [checkboxConsents, configs]);

  const alreadyHasConsent = useHasConsents(checkboxConsents);

  if (alreadyHasConsent && !alwaysShow) {
    return null;
  }

  return (
    <Checkbox
      onChange={onChange}
      checked={checked}
      name={name}
      multiline
      {...rest}
    >
      <Trans i18nKey={`form.consents.${consent}`} components={components} />
    </Checkbox>
  );
};
